import { lazy, ComponentType, Suspense } from 'react';
import { RouteObject } from 'react-router-dom';

// Helper function for lazy loading with error handling
const lazyLoad = <T extends ComponentType<any>>(
  importFunc: () => Promise<{ default: T }>
) => {
  return lazy(() => 
    importFunc().catch(error => {
      console.error('Failed to load component:', error);
      // Return a default fallback component or re-throw the error
      return Promise.resolve({ default: () => <div>Error loading component</div> });
    })
  );
};

const HomePage = lazyLoad(() => import('../pages/home/page'));
const SurveyPage = lazyLoad(() => import('../pages/survey/page'));
const MatchingPage = lazyLoad(() => import('../pages/matching/page'));
const ProfilePage = lazyLoad(() => import('../pages/profile/page'));
const AdminPage = lazyLoad(() => import('../pages/admin/page'));
const QuizPage = lazyLoad(() => import('../pages/quiz/page'));
const FinalVotePage = lazyLoad(() => import('../pages/final-vote/page'));
const FinalVoteInfoPage = lazyLoad(() => import('../pages/final-vote-info/page'));
const FinalVoteResultPage = lazyLoad(() => import('../pages/final-vote-result/page'));
const AdminVoteDetailsPage = lazyLoad(() => import('../pages/admin-vote-details/page'));
const FirstImpressionPage = lazyLoad(() => import('../pages/first-impression/page'));
const FirstImpressionResultPage = lazyLoad(() => import('../pages/first-impression-result/page'));
const OneOnOneMatchesPage = lazyLoad(() => import('../pages/one-on-one-matches/page'));
const OneOnOneChatPage = lazyLoad(() => import('../pages/one-on-one-chat/page'));
const ThirdConversationVotePage = lazyLoad(() => import('../pages/third-conversation-vote/page'));
const ThirdConversationPage = lazyLoad(() => import('../pages/third-conversation/page'));
const PresentationPage = lazyLoad(() => import('../pages/presentation/page'));
const NotFound = lazyLoad(() => import('../pages/NotFound'));

// Helper function to wrap components in Suspense
const withSuspense = (Component: React.ComponentType, fallback: React.ReactNode = <div>Loading...</div>) => (
  <Suspense fallback={fallback}>
    <Component />
  </Suspense>
);

const routes: RouteObject[] = [
  {
    path: '/',
    element: withSuspense(HomePage),
  },
  {
    path: '/survey',
    element: withSuspense(SurveyPage),
  },
  {
    path: '/matching',
    element: withSuspense(MatchingPage),
  },
  {
    path: '/profile',
    element: withSuspense(ProfilePage),
  },
  {
    path: '/admin',
    element: withSuspense(AdminPage),
  },
  {
    path: '/quiz',
    element: withSuspense(QuizPage),
  },
  {
    path: '/final-vote',
    element: withSuspense(FinalVotePage),
  },
  {
    path: '/final-vote-info',
    element: withSuspense(FinalVoteInfoPage),
  },
  {
    path: '/final-vote-result',
    element: withSuspense(FinalVoteResultPage),
  },
  {
    path: '/admin-vote-details',
    element: withSuspense(AdminVoteDetailsPage),
  },
  {
    path: '/first-impression',
    element: withSuspense(FirstImpressionPage),
  },
  {
    path: '/first-impression-result',
    element: withSuspense(FirstImpressionResultPage),
  },
  {
    path: '/one-on-one-matches',
    element: withSuspense(OneOnOneMatchesPage),
  },
  {
    path: '/one-on-one-chat',
    element: withSuspense(OneOnOneChatPage),
  },
  {
    path: '/third-conversation-vote',
    element: withSuspense(ThirdConversationVotePage),
  },
  {
    path: '/third-conversation',
    element: withSuspense(ThirdConversationPage),
  },
  {
    path: '/presentation',
    element: withSuspense(PresentationPage),
  },
  {
    path: '*',
    element: withSuspense(NotFound),
  },
];

export default routes;
